import PropTypes from 'prop-types';
import { useState } from 'react';
// @mui
import { alpha, styled } from '@mui/material/styles';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Avatar,
  Box,
  Chip,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
// components
import Iconify from '../../../components/iconify';
import { MotionInView, varFadeInUp } from '../../../components/animate';
import { SectionHeader } from '../common';

// ----------------------------------------------------------------------

const REVEAL_TRANSITION = { duration: 0.5, ease: [0.43, 0.13, 0.23, 0.96] };
const revealDelay = (index) => Math.min(index, 5) * 0.06;

const SubjectAccordion = styled(Accordion)(({ theme }) => ({
  mb: 1.5,
  overflow: 'hidden',
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${theme.palette.divider}`,
  backgroundImage: 'none',
  boxShadow: 'none',
  transition: 'box-shadow 0.3s ease, border-color 0.3s ease, transform 0.3s ease',
  '&::before': { display: 'none' },
  '&:hover': {
    borderColor: alpha(theme.palette.primary.main, 0.45),
    boxShadow: `0 10px 26px ${alpha(theme.palette.grey[900], 0.07)}`,
  },
  '&.Mui-expanded': {
    borderColor: alpha(theme.palette.primary.main, 0.45),
    boxShadow: `0 14px 32px ${alpha(theme.palette.grey[900], 0.1)}`,
  },
}));

const SummaryRow = styled(AccordionSummary)(({ theme }) => ({
  minHeight: 72,
  padding: theme.spacing(1.5, 2),
  gap: theme.spacing(2),
  alignItems: 'center',
  '& .MuiAccordionSummary-content': { margin: `${theme.spacing(1.5, 0, 1.5, 0)} !important` },
  '& .MuiAccordionSummary-expandIconWrapper': {
    color: theme.palette.text.disabled,
    transition: 'transform 0.35s cubic-bezier(0.43, 0.13, 0.23, 0.96), color 0.2s ease',
  },
  '&.Mui-expanded .MuiAccordionSummary-expandIconWrapper': {
    transform: 'rotate(180deg)',
    color: theme.palette.primary.main,
  },
}));

const DetailsRow = styled(AccordionDetails)(({ theme }) => ({
  padding: theme.spacing(0, 2, 2.25, 2),
  borderTop: `1px solid ${theme.palette.divider}`,
  paddingTop: theme.spacing(2.25),
}));

// Two-digit index chip so the numbering stays left-aligned as the count grows.
const indexLabel = (index) => String(index + 1).padStart(2, '0');

// ----------------------------------------------------------------------

SubjectCard.propTypes = {
  subject: PropTypes.object.isRequired,
  index: PropTypes.number,
};

function SubjectCard({ subject, index = 0 }) {
  const [expanded, setExpanded] = useState(false);

  const { slug, title, introduction, videocount, materialcount } = subject;

  const stats = [
    { id: 'videos', icon: 'ic:round-play-circle', value: videocount, label: 'videos' },
    { id: 'materials', icon: 'ic:round-pdf', value: materialcount, label: 'materials' },
  ].filter((stat) => Number.isFinite(stat.value));

  const handleChange = () => setExpanded((open) => !open);

  return (
    <MotionInView variants={varFadeInUp} transition={{ ...REVEAL_TRANSITION, delay: revealDelay(index) }}>
      <SubjectAccordion
        expanded={expanded}
        onChange={handleChange}
        disableGutters
        elevation={0}
        // Keeps the open panel announced correctly by assistive tech.
        slotProps={{
          heading: { 'aria-controls': `subject-panel-${slug}` },
          transition: { id: `subject-panel-${slug}` },
        }}
      >
        <SummaryRow
          expandIcon={<Iconify icon="eva:arrow-downward-outline" sx={{ width: 22, height: 22 }} />}
          aria-controls={`subject-panel-${slug}`}
          id={`subject-header-${slug}`}
        >
          <Avatar
            sx={{
              width: 40,
              height: 40,
              flexShrink: 0,
              fontSize: 13,
              fontWeight: 700,
              bgcolor: expanded ? 'primary.main' : 'grey.100',
              color: expanded ? 'common.white' : 'text.secondary',
              transition: 'background-color 0.3s ease, color 0.3s ease',
            }}
          >
            {indexLabel(index)}
          </Avatar>

          <Box sx={{ minWidth: 0, flexGrow: 1 }}>
            <Typography
              component="h3"
              variant="subtitle1"
              sx={{
                color: 'text.primary',
                fontWeight: 600,
                lineHeight: 1.35,
                overflow: 'hidden',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
              }}
            >
              {title}
            </Typography>

            {stats.length > 0 && (
              <Stack direction="row" spacing={1.5} sx={{ mt: 0.5, flexWrap: 'wrap' }} useFlexGap>
                {stats.map((stat) => (
                  <Stack key={stat.id} direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
                    <Iconify icon={stat.icon} width={15} sx={{ color: 'text.disabled', flexShrink: 0 }} />
                    <Typography variant="caption" sx={{ color: 'text.disabled', whiteSpace: 'nowrap' }}>
                      {`${stat.value} ${stat.label}`}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            )}
          </Box>

          {expanded && (
            <Chip
              label="Hide"
              size="small"
              sx={{ display: { xs: 'none', sm: 'inline-flex' }, height: 24, fontSize: 11, flexShrink: 0 }}
            />
          )}
        </SummaryRow>

        <DetailsRow>
          <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.75 }}>
            {introduction || 'Details for this module will be published shortly.'}
          </Typography>
        </DetailsRow>
      </SubjectAccordion>
    </MotionInView>
  );
}

// ----------------------------------------------------------------------

CourseCurriculum.propTypes = {
  subjects: PropTypes.array,
  isLoading: PropTypes.bool,
  eyebrow: PropTypes.string,
  title: PropTypes.string,
  description: PropTypes.string,
  titleComponent: PropTypes.string,
  sx: PropTypes.object,
};

// The syllabus behind a course: one expandable row per subject, numbered in
// the order the backend returns them (CourseSubject.partno).
export default function CourseCurriculum({
  subjects,
  isLoading = false,
  eyebrow = 'Syllabus',
  title = 'Course Curriculum',
  description,
  titleComponent = 'h2',
  sx,
}) {
  const list = Array.isArray(subjects) ? subjects.filter((subject) => subject?.slug || subject?.title) : [];

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: { xs: 6, md: 8 } }}>
        <CircularProgress size={32} thickness={4} />
      </Box>
    );
  }

  if (!list.length) {
    return null;
  }

  return (
    <Box component="section" sx={{ width: '100%', ...sx }}>
      <SectionHeader
        eyebrow={eyebrow}
        title={title}
        titleComponent={titleComponent}
        description={description}
        align="left"
        sx={{ mb: { xs: 2, md: 3 } }}
      />

      <Box>
        {list.map((subject, index) => (
          <SubjectCard key={subject.slug ?? index} subject={subject} index={index} />
        ))}
      </Box>
    </Box>
  );
}
